import { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MainNav from '@/components/layout/AppNavbar';
import DepositModal from '../components/DepositModal';
import WithdrawModal from '../components/WithdrawModal';
import WithdrawConfirmModal from '../components/WithdrawConfirmModal';
import TransactionTable from '../components/TransactionTable';
import MessageModal from '@/components/ui/MessageModal';
import StatusModal from '@/components/ui/StatusModal';
import { fetchWalletInfo } from '@/shared/store/slices/walletSlice';

function WalletPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const depositModalRef = useRef();
  const withdrawModalRef = useRef();
  const withdrawConfirmModalRef = useRef();
  const successModalRef = useRef();
  const transactionTableRef = useRef();
  const statusModalRef = useRef(null);

  // 從 Redux 取得錢包資訊。餘額未取得（初次載入中或載入失敗）時一律不給數字：
  // 提領的金額上限就是這個餘額，拿 NaN 去比會讓確認鍵沒有任何理由地永遠是灰的
  const rawBalance = useSelector(state => state.wallet.balance);
  const isBalanceReady = rawBalance != null;
  const balance = isBalanceReady ? parseFloat(rawBalance) : null;

  // 取得當前操作的偶像 ID
  const currentIdolId = useSelector(state => state.user.idolId);

  // 錯誤狀態管理：statusCode 只寫入不清空，關閉後留著讓淡出動畫仍有文案可顯示；
  // 重複發生同一個碼靠 show() 重新開啟，不依賴 state 變化
  const [currentStatusCode, setCurrentStatusCode] = useState('');

  const showError = useCallback((statusCode) => {
    // 非字串＝拿不到狀態碼。除了一般的 undefined，RTK 的 unwrap() 在
    // rejectWithValue(undefined) 時會丟 { message: 'Rejected' } 物件
    // （DepositModal／WithdrawConfirmModal 的 catch 是無條件轉呼叫過來的），
    // 讓它流進 StatusModal 會在 getErrorMessageKey 對物件呼叫 toLowerCase 而整棵樹崩掉。
    // 彈窗無碼可顯示，至少留 log 讓問題暴露。
    if (typeof statusCode !== 'string') {
      console.error('Unexpected error without status code', statusCode);
      return;
    }
    // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
    if (statusCode === 'NETWORK_ERROR') return;
    setCurrentStatusCode(statusCode);
    statusModalRef.current?.show();
  }, []);

  // 提領處理時間
  const [processingHours, setProcessingHours] = useState(48);

  // 餘額載入失敗：錯誤 modal 關掉後畫面不能只剩一個永遠轉不完的 spinner，要留重試的出口
  const [isLoadFailed, setLoadFailed] = useState(false);

  // 載入錢包資訊。用 unwrap() 接「這一次 dispatch」的結果——錯誤不放進 store，
  // 否則會讀到別處（解鎖／訂閱 modal）留下的舊錯誤，一進頁面就先彈一次過期的錯誤 modal
  const loadWallet = useCallback(() => {
    if (!currentIdolId) return;
    setLoadFailed(false);
    dispatch(fetchWalletInfo(currentIdolId))
      .unwrap()
      .catch((statusCode) => {
        setLoadFailed(true);
        showError(statusCode);
      });
  }, [currentIdolId, dispatch, showError]);

  useEffect(() => {
    loadWallet();
  }, [loadWallet]);

  const handleDeposit = () => {
    depositModalRef.current?.show();
  };

  const handleWithdraw = () => {
    withdrawModalRef.current?.show();
  };

  // 處理充值錯誤
  const handleDepositError = showError;

  // 處理提領確認
  const handleWithdrawConfirm = (withdrawData) => {
    // 保存處理時間用於成功訊息
    if (withdrawData.processingHours) {
      setProcessingHours(withdrawData.processingHours);
    }
    withdrawConfirmModalRef.current?.show(withdrawData);
  };

  // 處理提領錯誤
  const handleWithdrawError = showError;

  // 處理提領成功
  const handleWithdrawSuccess = () => {
    successModalRef.current?.show();
    // 重新載入錢包資訊
    loadWallet();
    // 刷新交易紀錄
    transactionTableRef.current?.refresh();
  };

  return (
    <>
      <MainNav />
      <main className="nav-padding">
        <div className="container-lg py-4">
          <h2 className="mb-4">{t('wallet')}</h2>

          {/* 錢包餘額卡片 */}
          <div className="row mb-5 align-items-start">
            {/* 按鈕在卡片右側（桌面版）/ 上方（手機版） */}
            <div className="col-12 col-md-auto ms-md-auto order-1 order-md-2 mb-3 mb-md-0">
              <div className="d-flex gap-3 justify-content-end">
                <button
                  className="btn btn-primaryF rounded-pill"
                  onClick={handleDeposit}
                  disabled={!currentIdolId}
                >
                  {t('deposit')}
                </button>
                <button
                  className="btn btn-primaryF rounded-pill"
                  onClick={handleWithdraw}
                  disabled={!currentIdolId || !isBalanceReady}
                >
                  {t('withdraw')}
                </button>
              </div>
            </div>

            {/* 可用餘額卡片 */}
            <div className="col-12 order-2 order-md-1" style={{ maxWidth: '500px' }}>
              <div className="bg-white rounded p-5">
                <div className="d-flex align-items-center gap-2 mb-3 justify-content-center">
                  <FontAwesomeIcon icon={["fas", "comments-dollar"]} className="text-primary" size="2x" />
                  <h5 className="mb-0 fw-bold">{t('available_balance')}</h5>
                </div>
                <div className="d-flex align-items-end justify-content-center my-4">
                  {/* 沒有 idolId 就抓不到錢包（未登入，或 session 中途被 SYSTEM_1004 清掉）。
                      這時給重試鍵是騙人的——loadWallet 會直接 early return，按了毫無反應 */}
                  {!currentIdolId ? (
                    <p className="mb-0 text-center">{t('error_system_1004')}</p>
                  ) : isBalanceReady ? (
                    <>
                      <p className="display-4 fw-bold mb-0 text-primary">₣{balance}</p>
                      <span className="ms-2">FCoin</span>
                    </>
                  ) : isLoadFailed ? (
                    <div className="text-center">
                      <p className="mb-3">{t('load_failed', { type: t('wallet') })}</p>
                      <button className="btn btn-primaryF rounded-pill" onClick={loadWallet}>
                        {t('retry_load')}
                      </button>
                    </div>
                  ) : (
                    <span className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">{t('loading')}</span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* 交易紀錄。沒有 idolId 就抓不到資料，整張表不渲染——
              否則會與上方的「請重新登入」並存一組篩選器和「無數據」，訊息互相矛盾 */}
          {currentIdolId && <TransactionTable ref={transactionTableRef} />}
        </div>
      </main>

      {/* 充值 Modal */}
      <DepositModal
        ref={depositModalRef}
        onError={handleDepositError}
      />

      {/* 提領 Modal */}
      <WithdrawModal
        ref={withdrawModalRef}
        onConfirm={handleWithdrawConfirm}
        onError={handleWithdrawError}
        balance={balance}
      />

      {/* 提領確認 Modal */}
      <WithdrawConfirmModal
        ref={withdrawConfirmModalRef}
        onSuccess={handleWithdrawSuccess}
        onError={handleWithdrawError}
      />

      {/* 成功訊息 Modal */}
      <MessageModal ref={successModalRef} status="success">
        <h5 className="mb-3">{t('withdraw_request_submitted')}</h5>
        <p className="mb-2">{t('status_review_time_dynamic', { hours: processingHours })}</p>
        <p className="mb-0">{t('check_progress_in_transaction_history')}</p>
      </MessageModal>

      {/* Status Modal - 動態錯誤處理。常駐掛載：條件掛載會在淡出動畫途中被卸載，
          bootstrap 來不及還原 body 的捲動鎖，關掉後整頁可能捲不動 */}
      <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />
    </>
  );
}

export default WalletPage;
